import React, { useEffect, useState, useRef, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { fetchItems } from "../../utils/fetchItemData";
import Select from "react-select";
import Pagination from "../../components/Pagination";
import { useDebounce } from "use-debounce";
import { useModalStack } from "../../utils/modalContext";
import { useCurrencies } from "../../hooks/useCurrencies";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const ItemLookup = () => {
  const navigate = useNavigate();
  const query = useQuery();

  const [items, setItems] = useState([]);
  const [filtersVisible, setFiltersVisible] = useState(true); //hide filters on page
  const [searchTerm, setSearchTerm] = useState(query.get("search") || "");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [itemType, setItemType] = useState(false); //(F)Items or (T)UGC
  const [craftables, setCraftables] = useState([]); //industry filter
  const [selectedCraftable, setSelectedCraftable] = useState(query.get("craftable") || ""); //industry filter
  const [selectedIndustry, setSelectedIndustry] = useState(query.get("industry") || ""); //industry filter
  const [types, setTypes] = useState([]); //category filter
  const [typeOptions, setTypeOptions] = useState([]); //category filter
  const [selectedTypes, setSelectedTypes] = useState(query.getAll("types") || []); //category filter
  const [selectedCreator, setSelectedCreator] = useState(query.get("creator") || ""); //ugc creator filter
  const [ugcCreators, setUGCCreators] = useState([]); //ugc creator filter
  const [obtainables, setObtainables] = useState([]); //How to Obtain item filter
  const [selectedObtainable, setSelectedObtainable] = useState(query.get("obtainable") || "");
  const [limitedFilterState, setLimitedFilterState] = useState(parseInt(query.get("limited") || "0", 10)); // 0: show all, 1: exclude no tier, 2: exclude tier
  const [tierFilterState, setTierFilterState] = useState(parseInt(query.get("itemTier") || "0", 10)); // 0: show all, 1: exclude no tier, 2: exclude tier
  const [currentPage, setCurrentPage] = useState(parseInt(query.get("page") || "1", 10)); //Pagination
  const itemsPerPage = 50; //Pagination
  const [sortColumn, setSortColumn] = useState("profit"); //Sort Feature
  const [sortOrder, setSortOrder] = useState("desc"); //Sort Feature
  const selectRef = useRef(null); //to clear the category filter properly
  const { pushModal, popModal } = useModalStack();
  const [coinsIcon, setCoinsIcon] = useState(null);
  const { data } = useCurrencies();

  useEffect(() => {
    if (!data) return;

    const coin = data.find((cur) => cur.id === "cur_coins");
    if (coin) {
      setCoinsIcon(coin.image);
    }
  }, [data]);

  //Item Modal & Navigation
  const handleOpenModal = async (itemIndex) => {
    const absoluteIndex = indexOfFirstItem + itemIndex;
    const item = filteredItems[absoluteIndex];
    if (!item) return;

    try {
      pushModal({
        id: `item-${item._id}`,
        type: "itemModal",
        props: {
          item,
          index: absoluteIndex,
          totalItems: filteredItems.length,
          showNavigation: true,
          onNavigate: (direction) => handleNavigate(direction, absoluteIndex),
          onClose: () => popModal(),
        },
      });
    } catch (err) {
      console.error("Failed to open item modal:", err);
    }
  };

  const handleNavigate = (direction, currentIndex) => {
    const newIndex = direction === "next" ? (currentIndex + 1) % filteredItems.length : (currentIndex - 1 + filteredItems.length) % filteredItems.length;

    const newItem = filteredItems[newIndex];
    if (!newItem) return;

    popModal();

    pushModal({
      id: `item-${newItem._id}`,
      type: "itemModal",
      props: {
        item: newItem,
        index: newIndex,
        totalItems: filteredItems.length,
        showNavigation: true,
        onNavigate: (dir) => handleNavigate(dir, newIndex),
        onClose: () => popModal(),
      },
    });
  };

  //Sets up the URL Search Params in the URL
  useEffect(() => {
    const params = new URLSearchParams();

    // Add parameters only if they differ from their defaults
    if (debouncedSearchTerm) params.set("search", debouncedSearchTerm);
    if (selectedCraftable) params.set("craftable", selectedCraftable);
    if (selectedIndustry) params.set("industry", selectedIndustry);
    if (selectedTypes.length > 0) {
      params.delete("types"); // Remove existing types
      selectedTypes.forEach((type) => params.append("types", type));
    }
    if (selectedObtainable) params.set("obtainable", selectedObtainable);
    if (selectedCreator) params.set("creator", selectedCreator);
    if (tierFilterState !== 0) params.set("itemTier", tierFilterState); // Only add if not default (0)
    if (limitedFilterState !== 0) params.set("limited", limitedFilterState); // Only add if not default (0)
    if (currentPage !== 1) params.set("page", currentPage); // Only add if not default (1)

    navigate({ search: params.toString() });
  }, [
    debouncedSearchTerm,
    selectedCraftable,
    selectedIndustry,
    selectedCreator,
    selectedTypes,
    selectedObtainable,
    tierFilterState,
    limitedFilterState,
    currentPage,
    navigate,
  ]);

  //get items based on item Type
  useEffect(() => {
    const itemId = null;
    fetchItems({ itemId, setItems, setCraftables, setTypes, itemType, setUGCCreators, setObtainables });
  }, [itemType]);

  //format market differences (up/down trends)
  function formatDiff(value, percent = false) {
    if (value === Infinity) return "∞";
    if (value === -Infinity) return "-∞";
    if (value === null) return "--";
    const textColor = value > 0 ? "text-green-800" : "text-[var(--red)]";

    return (
      <span className={`${textColor} font-bold`}>
        {value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
        {percent ? `%` : ``}
      </span>
    );
  }

  //clean items for sort
  const clean = (val, columnName) => {
    if (val === null || val === undefined) return null;

    // Special handling for energy column (if needed)
    if (columnName === "energy" && (val === 0 || val === "0")) return null;

    if (typeof val === "string") {
      const trimmed = val.trim().toLowerCase();
      if (trimmed === "n/a" || trimmed === "" || trimmed === "--") return null;

      // Try to parse numbers with commas (marketplace values)
      const num = parseFloat(trimmed.replace(/,/g, ""));
      if (!isNaN(num)) return num; // Return as number if valid

      return trimmed; // Otherwise return original string
    }

    return val; // Numbers/other values pass through
  };

  //filters
  const handleTierFilterToggle = () => setTierFilterState((prev) => (prev + 1) % 3); // Cycles through 0, 1, 2
  const handleCreatorChange = (selectedOption) => setSelectedCreator(selectedOption.value);
  const handleLimitedFilterLogic = () => setLimitedFilterState((prev) => (prev + 1) % 3); // Cycles through 0, 1, 2
  const handleCraftableChange = (selectedOption) => setSelectedCraftable(selectedOption.value);
  const handleObtainableChange = (selectedOption) => setSelectedObtainable(selectedOption.value);
  const handlePageChange = (newPage) => setCurrentPage(newPage);
  const handleTypeChange = (selectedOptions) => setSelectedTypes(selectedOptions.map((option) => option.value));

  const handleSort = (column) => {
    const order = sortColumn === column && sortOrder === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortOrder(order);
  };

  useEffect(() => {
    const options = types.map((type) => ({ value: type, label: type }));
    setTypeOptions([...options]);
  }, [types]);

  const filteredItems = useMemo(() => {
    return items
      .filter((item) => {
        // Skip search filter if searchTerm is empty
        if (!debouncedSearchTerm) return true;
        return item?.name?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) || item?.id?.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
      })
      .filter((item) => {
        // Skip craftable filter if selectedCraftable is empty
        if (!selectedCraftable) return true;
        return selectedCraftable === "craftable" ? item.craftable !== "Not Craftable" : item.craftable === selectedCraftable;
      })
      .filter((item) => {
        // Skip creator filter if selectedCreator is empty
        if (!selectedCreator) return true;
        return item.ugc?.creatorname === selectedCreator;
      })
      .filter((item) => {
        // Skip industry filter if selectedIndustry is empty
        if (!selectedIndustry) return true;
        return item.craftable?.toLowerCase() === selectedIndustry.toLowerCase();
      })
      .filter((item) => {
        // Skip type filter if no types are selected
        if (selectedTypes.length === 0) return true;
        return selectedTypes.some((selectedType) => item.category.split(",").some((type) => type.trim() === selectedType));
      })
      .filter((item) => {
        // Skip tier filter if tierFilterState is default (0)
        if (tierFilterState === 0) return true;
        if (tierFilterState === 1) return item.itemTier !== "Untiered"; // Exclude items with no tier
        if (tierFilterState === 2) return item.itemTier === "Untiered" || !item.itemTier; // Exclude items with a tier
        return true;
      })
      .filter((item) => {
        // Skip limited filter if limitedFilterState is default (0)
        if (limitedFilterState === 0) return true;
        if (limitedFilterState === 1) return item.notes?.[0]?.limited; // Exclude items that are not limited
        if (limitedFilterState === 2) return !item.notes?.[0]?.limited; // Exclude items that are limited
        return true;
      })
      .filter((item) => {
        // Skip obtainable filter if selectedObtainable is empty
        if (!selectedObtainable) return true;
        return item.obtainable && item.obtainable.includes(selectedObtainable);
      })
      .sort((a, b) => {
        const getNestedValue = (obj, path) => {
          return path.split(".").reduce((acc, part) => acc?.[part], obj);
        };

        const valueA = getNestedValue(a, sortColumn);
        const valueB = getNestedValue(b, sortColumn);

        const cleanedA = clean(valueA, sortColumn);
        const cleanedB = clean(valueB, sortColumn);

        const isANull = cleanedA === null || cleanedA === undefined;
        const isBNull = cleanedB === null || cleanedB === undefined;

        if (isANull && isBNull) return 0;
        if (isANull) return 1; // Push nulls down
        if (isBNull) return -1;

        // Numeric comparison (if both are numbers)
        if (typeof cleanedA === "number" && typeof cleanedB === "number") {
          const isAZero = cleanedA === 0;
          const isBZero = cleanedB === 0;

          if (isAZero && isBZero) return 0;
          if (isAZero) return 1; // push 0 down
          if (isBZero) return -1;

          return sortOrder === "asc" ? cleanedA - cleanedB : cleanedB - cleanedA;
        }

        // Fallback: String comparison
        const strA = String(cleanedA);
        const strB = String(cleanedB);
        return sortOrder === "asc" ? strA.localeCompare(strB) : strB.localeCompare(strA);
      });
  }, [
    items,
    debouncedSearchTerm,
    selectedCraftable,
    selectedIndustry,
    selectedCreator,
    selectedTypes,
    sortColumn,
    sortOrder,
    tierFilterState,
    limitedFilterState,
    selectedObtainable,
  ]);

  const totalItems = filteredItems.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(totalPages, currentPage + 2);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const clearAllFilters = () => {
    setSearchTerm("");
    setSelectedCraftable("");
    setSelectedCreator("");
    setSelectedIndustry("");
    setSelectedTypes([]);
    setCurrentPage(1);
    setTierFilterState(0);
    setLimitedFilterState(0);
    setSelectedObtainable("");

    if (selectRef.current) {
      selectRef.current.clearValue();
    }

    // Update URL to reflect cleared filters
    navigate({ search: "" }); // Clear all query parameters
  };

  return (
    <div className="panel-style">
      <h1 className="panel-heading">{!itemType ? "Item" : "UGC"} Lookup</h1>

      <div className="component-style">
        <div className="mb-2">
          <div className="flex justify-between">
            {/* Left Side: Hide Filters Button and Total Items */}
            <div className="flex items-center space-x-2">
              <button onClick={() => setFiltersVisible(!filtersVisible)} className="p-1 h-4 w-4 flex items-center justify-center rounded text-xs panel-button">
                {filtersVisible ? "-" : "+"}
              </button>
              <p className="text-xs leading-none">Total Items: {totalItems}</p>
            </div>

            {/* Right Side: UGC/Item Toggle */}
            <div className="flex items-center space-x-1">
              {/* Label for Items */}
              <span className={`text-xs font-medium ${!itemType ? "text-[var(--text3-color)]" : "text-[var(--text-color)]"}`}>Items</span>

              {/* Toggle Switch */}
              <button
                onClick={() => {
                  setItemType(!itemType);
                  clearAllFilters();
                }}
                className={`relative w-6 h-3 rounded-full p-0.5 transition-colors duration-200 focus:outline-none ${
                  itemType ? "bg-[var(--button-bg-color)]" : "bg-[var(--button3-bg-color)]"
                }`}
              >
                {/* Toggle Circle */}
                <div
                  className={`m-[-1] absolute w-1 h-1 bg-[var(--level-bars)] rounded-full shadow-md transform transition-transform duration-200 ${
                    itemType ? "translate-x-4" : "translate-x-0"
                  }`}
                ></div>
              </button>

              {/* Label for UGC */}
              <span className={`text-xs font-medium ${itemType ? "text-[var(--text3-color)]" : "text-[var(--text-color)]"}`}>UGC</span>
            </div>
          </div>

          {/* Filters Section */}
          {filtersVisible && (
            <div className="filters-panel">
              <div className="container mx-auto p-1 text-sm">
                <div className="flex flex-col md:flex-row items-center justify-between space-y-2 md:space-y-0 md:space-x-2">
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="p-2 border rounded w-full flex-1 select-forms"
                  />
                  {!itemType && (
                    <>
                      <Select
                        onChange={handleCraftableChange}
                        value={selectedCraftable ? { label: selectedCraftable, value: selectedCraftable } : { label: "All Industries", value: "" }}
                        options={[
                          { label: "All Industries", value: "" },
                          { label: "Craftable", value: "craftable" },
                          ...craftables.map((craftable) => ({
                            label: craftable,
                            value: craftable,
                          })),
                        ]}
                        className="select-forms m-1 z-[40] w-full flex-1"
                      />
                      <Select
                        onChange={handleObtainableChange}
                        value={selectedObtainable ? { label: selectedObtainable, value: selectedObtainable } : { label: "Methods to Acquire..", value: "" }}
                        options={[
                          { label: "Methods to Acquire...", value: "" },
                          ...obtainables.map((obtainable) => ({
                            label: obtainable,
                            value: obtainable,
                          })),
                        ]}
                        className="select-forms m-1 z-30 w-full flex-1"
                        placeholder="Methods to Acquire.."
                      />
                    </>
                  )}
                  <Select
                    ref={selectRef}
                    isMulti
                    onChange={handleTypeChange}
                    options={[{ label: "Item Categories...", value: "" }, ...typeOptions]}
                    placeholder="Item categories..."
                    className="select-forms w-full z-[36] flex-1 text-xs"
                    classNamePrefix="select"
                  />
                  {itemType && (
                    <Select
                      onChange={handleCreatorChange}
                      value={selectedCreator ? { label: selectedCreator, value: selectedCreator } : { label: "UGC Creators...", value: "" }}
                      options={[
                        { label: "UGC Creators...", value: "" },
                        ...ugcCreators.map((creator) => ({
                          label: creator,
                          value: creator,
                        })),
                      ]}
                      className="select-forms m-1 z-30 w-full flex-1"
                      placeholder="UGC Creators..."
                    />
                  )}
                </div>
              </div>

              <div className="flex flex-col md:flex-row items-center justify-between space-y-2 md:space-y-0 md:space-x-2 md:ml-10 md:mr-10 text-xs">
                {/* Filter Buttons Container with Fixed Width */}
                <div className="w-full md:w-1/2 flex gap-2">
                  {!itemType && (
                    <>
                      <button onClick={handleLimitedFilterLogic} className="panel-button text-xs">
                        {limitedFilterState === 0 && (
                          <>
                            <i className="fa-solid fa-circle-dot fa-xs" style={{ color: "#c2a8c1" }} /> Showing all items (Limited & Unlimited)
                          </>
                        )}
                        {limitedFilterState === 1 && (
                          <>
                            <i className="fa-regular fa-circle-dot fa-xs" style={{ color: "#c2a8c1" }} /> Showing Items that are Limited
                          </>
                        )}
                        {limitedFilterState === 2 && (
                          <>
                            <i className="fa-solid fa-circle-notch fa-xs" style={{ color: "#c2a8c1" }} /> Showing Items that are not Limited
                          </>
                        )}
                      </button>
                      <button onClick={handleTierFilterToggle} className="panel-button text-xs">
                        {tierFilterState === 0 && (
                          <>
                            <i className="fa-solid fa-circle-dot fa-xs" style={{ color: "#c2a8c1" }} /> Showing all items (Tier & No Tier)
                          </>
                        )}
                        {tierFilterState === 1 && (
                          <>
                            <i className="fa-regular fa-circle-dot fa-xs" style={{ color: "#c2a8c1" }} /> Showing Tiered Items
                          </>
                        )}
                        {tierFilterState === 2 && (
                          <>
                            <i className="fa-solid fa-circle-notch fa-xs" style={{ color: "#c2a8c1" }} /> Showing Untiered Items
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>

                {/* Clear All Button */}
                <button onClick={clearAllFilters} className="panel-button text-xs">
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="overflow-auto h-[90vh] md:h-[80vh] lg:h-[55vh]">
          <table className="market-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("name")}>Name {sortColumn === "name" && (sortOrder === "asc" ? "↑" : "↓")}</th>
                {itemType && (
                  <>
                    <th onClick={() => handleSort("ugc.creatorname")}>Creator {sortColumn === "ugc.creatorname" && (sortOrder === "asc" ? "↑" : "↓")}</th>
                    <th onClick={() => handleSort("category")}>Category {sortColumn === "category" && (sortOrder === "asc" ? "↑" : "↓")}</th>
                  </>
                )}
                {!itemType && (
                  <>
                    <th onClick={() => handleSort("energyValue")}>Energy {sortColumn === "energyValue" && (sortOrder === "asc" ? "↑" : "↓")}</th>
                    <th onClick={() => handleSort("craftingCost")}>
                      <div className="flex flex-col">
                        <span>Cost</span>
                        <span>(w/energy)</span>
                      </div>{" "}
                      {sortColumn === "craftingCost" && (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th onClick={() => handleSort("profit")}>Profit {sortColumn === "profit" && (sortOrder === "asc" ? "↑" : "↓")}</th>
                  </>
                )}
                <th onClick={() => handleSort("marketPrice")}>MP Price{sortColumn === "marketPrice" && (sortOrder === "asc" ? "↑" : "↓")}</th>
                <th onClick={() => handleSort("marketPieces")}>MP Qty{sortColumn === "marketPieces" && (sortOrder === "asc" ? "↑" : "↓")}</th>
                <th className="-reduce" colSpan="2" onClick={() => handleSort("history.change1h")}>
                  <div className="flex flex-col items-center">
                    <span>1H Difference</span>
                    <span>Price/QTY {sortColumn === "history.change1h" && (sortOrder === "asc" ? "↑" : "↓")}</span>
                  </div>
                </th>
                <th className="-reduce" colSpan="2" onClick={() => handleSort("history.change1d")}>
                  <div className="flex flex-col items-center">
                    <span>1D Difference</span>
                    <span>Price/QTY {sortColumn === "history.change1d" && (sortOrder === "asc" ? "↑" : "↓")}</span>
                  </div>
                </th>
                <th className="-reduce" colSpan="2" onClick={() => handleSort("history.change7d")}>
                  <div className="flex flex-col items-center">
                    <span>7D Difference</span>
                    <span>Price/QTY {sortColumn === "history.change7d" && (sortOrder === "asc" ? "↑" : "↓")}</span>
                  </div>
                </th>
                <th className="-reduce" colSpan="2" onClick={() => handleSort("history.change15d")}>
                  <div className="flex flex-col items-center">
                    <span>15D Difference</span>
                    <span>Price/QTY {sortColumn === "history.change15d" && (sortOrder === "asc" ? "↑" : "↓")}</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((item, index) => (
                <tr key={item.id}>
                  <td className="modal-click" onClick={() => handleOpenModal(index)}>
                    <img src={item.image} alt={item.name} className="h-5 w-5 inline" /> {item.name}
                  </td>
                  {itemType && (
                    <>
                      <td className="text-xs mx-auto"><Link to={`/lookup/players/${item.ugc?.creatorname}`} target="_blank" rel="noreferrer">{item.ugc?.creatorname}</Link></td>
                      <td>{item.category}</td>
                    </>
                  )}
                  {!itemType && (
                    <>
                      <td>{item.energyValue}</td>
                      <td>
                        {item.craftingCost && (
                          <>
                            <img src={coinsIcon} alt="coin" className="w-1 h-1 inline" />{" "}
                            {item.craftingCost?.toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2,
                            })}
                          </>
                        )}
                      </td>
                      <td>
                        {item.profit && (
                          <>
                            <img src={coinsIcon} alt="coin" className="w-1 h-1 inline" />{" "}
                            {item.profit?.toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2,
                            })}
                          </>
                        )}
                      </td>
                    </>
                  )}

                  <td>
                    <img src={coinsIcon} alt="coin" className="w-1 h-1 inline" /> {item.marketPrice}
                  </td>
                  <td>{item.marketPieces.toLocaleString()}</td>
                  <td className="-reduce">{formatDiff(item.history.change1h, true)}</td>
                  <td className="-reduce">{formatDiff(item.history.qty1h)}</td>
                  <td className="-reduce">{formatDiff(item.history.change1d, true)}</td>
                  <td className="-reduce">{formatDiff(item.history.qty1d)}</td>
                  <td className="-reduce">{formatDiff(item.history.change7d, true)}</td>
                  <td className="-reduce">{formatDiff(item.history.qty7d)}</td>
                  <td className="-reduce">{formatDiff(item.history.change15d, true)}</td>
                  <td className="-reduce">{formatDiff(item.history.qty15d)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-[var(--filters-panel)] rounded-lg shadow p-2">
          <Pagination totalPages={totalPages} currentPage={currentPage} onPageChange={handlePageChange} startPage={startPage} endPage={endPage} />
        </div>
      </div>
    </div>
  );
};

export default ItemLookup;
